import { useMemo, useState } from "react";
import {
  filterClassificationRows,
  getUniqueVendors,
  sortClassificationRows,
} from "@/components/downtime-classification/downtime-classification-utils.js";

/**
 * Shared search / vendor-filter / sort state for classification grids.
 * Vendor filter is controlled from a parent via `selectedVendors` + `onSelectedVendorsChange`.
 * @param {object[]} rows - Rows to filter and sort (may already include pending fields)
 * @param {Object} [options]
 * @param {string} [options.initialSortColumn="Vendor"]
 * @param {'asc'|'desc'} [options.initialSortDirection="asc"]
 * @param {string[]|null} [options.selectedVendors] - Controlled vendor filter (`null` = all)
 * @param {(vendors: string[]|null) => void} [options.onSelectedVendorsChange]
 * @returns {{
 *   search: string,
 *   setSearch: (value: string) => void,
 *   selectedVendors: string[]|null,
 *   setSelectedVendors: (vendors: string[]|null) => void,
 *   sortColumn: string,
 *   sortDirection: 'asc'|'desc',
 *   handleSort: (column: string) => void,
 *   vendorOptions: string[],
 *   filteredAndSortedRows: object[],
 * }}
 */
export function useClassificationGrid(
  rows,
  {
    initialSortColumn = "Vendor",
    initialSortDirection = /** @type {'asc'|'desc'} */ ("asc"),
    selectedVendors: controlledSelectedVendors,
    onSelectedVendorsChange,
  } = {}
) {
  const [search, setSearch] = useState("");
  const [internalSelectedVendors, setInternalSelectedVendors] = useState(
    /** @type {string[]|null} */ (null)
  );
  const isVendorFilterControlled =
    typeof onSelectedVendorsChange === "function";
  const selectedVendors = isVendorFilterControlled
    ? (controlledSelectedVendors ?? null)
    : internalSelectedVendors;
  const setSelectedVendors = isVendorFilterControlled
    ? onSelectedVendorsChange
    : setInternalSelectedVendors;
  const [sortColumn, setSortColumn] = useState(initialSortColumn);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  const vendorOptions = useMemo(() => getUniqueVendors(rows), [rows]);

  /**
   * Handles column header click for sorting.
   * @param {string} column
   */
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      // Counts and assigned classifications are most useful largest / assigned-first.
      const defaultDesc =
        column === "IsMech"
        || column === "DowntimeEventCount"
        || column === "DowntimeHoursCount";
      setSortDirection(defaultDesc ? "desc" : "asc");
    }
  };

  const filteredAndSortedRows = useMemo(() => {
    const filtered = filterClassificationRows(rows, search, selectedVendors);
    return sortClassificationRows(filtered, sortColumn, sortDirection);
  }, [rows, search, selectedVendors, sortColumn, sortDirection]);

  return {
    search,
    setSearch,
    selectedVendors,
    setSelectedVendors,
    sortColumn,
    sortDirection,
    handleSort,
    vendorOptions,
    filteredAndSortedRows,
  };
}
